// (C) 2022 GoodData Corporation
import { ResultColors } from "./gooddata_format.js";
export class InvalidArgumentError extends Error {
    constructor(message) {
        super(message);
        this.name = "InvalidArgumentError";
        // Set the prototype explicitly (for instanceof to work correctly)
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
export const isInvalidArgumentError = (error) => {
    return error instanceof InvalidArgumentError;
};
/**
 * Utility class to iterate over characters in a string.
 */
export class StringCharacterIterator {
    constructor(str, begin, end, pos = begin) {
        this.str = str;
        this.pos = pos;
        this.begin = begin;
        this.end = end;
    }
    hasNext() {
        return this.pos < this.end;
    }
    hasPrevious() {
        return this.pos > this.begin;
    }
    next() {
        if (this.hasNext()) {
            const char = this.str.charAt(this.pos);
            this.pos++;
            return char;
        }
        return "";
    }
    previous() {
        if (this.hasPrevious()) {
            this.pos--;
            return this.str.charAt(this.pos);
        }
        return "";
    }
    getIndex() {
        return this.pos;
    }
}
/**
 * Extracts color coding information from a string. The code will look for named-color tags, custom color and
 * custom color tags in a string. The values from these tags are extracted and returned categorized in a struct.
 *
 * The input string is left as-is.
 *
 * @param colorMatcher - Regex matcher for named color tags.
 * @param customColorMatcher - Regex matcher for custom color tags.
 * @param customBgColorMatcher - Regex matcher for custom bg color tags.
 * @param str - Input string.
 * @returns Categorized coloring.
 */
export function extractColorCoding(colorMatcher, customColorMatcher, customBgColorMatcher, str) {
    const result = new ResultColors();
    const colorMatch = str.match(colorMatcher);
    if (colorMatch) {
        result.color = colorMatch[1].toLowerCase();
    }
    const customColorMatch = str.match(customColorMatcher);
    if (customColorMatch) {
        result.customColor = customColorMatch[1].toLowerCase();
    }
    const customBgColorMatch = str.match(customBgColorMatcher);
    if (customBgColorMatch) {
        result.customBgColor = customBgColorMatch[1].toLowerCase();
    }
    return result;
}
/**
 * Removes all color coding tags from a string.
 *
 * @param matchers - Regex matchers.
 * @param str - String to remove color coding tags from.
 * @returns New string.
 */
export function stripColors(matchers, str) {
    const colorMatcher = matchers.getColorMatcher();
    const customColorMatcher = matchers.getAnyCustomColorMatcher();
    const noColors = str.replace(new RegExp(colorMatcher, "gi"), "");
    return noColors.replace(new RegExp(customColorMatcher, "gi"), "");
}
/**
 *  Strings representing constants passed by caller in format options.
 */
class IcuFormatConstants {
    constructor(options) {
        this.thousandsSeparator = options.thousandsSeparator || IcuFormatConstants.DefaultThousandSeparator;
        this.decimalSeparator = options.decimalSeparator || IcuFormatConstants.DefaultDecimalSeparator;
        this.nanString = options.nanString || IcuFormatConstants.DefaultNaNString;
        this.positiveInfinity = options.positiveInfinity || IcuFormatConstants.DefaultPositiveInfinity;
        this.negativeInfinity = options.negativeInfinity || IcuFormatConstants.DefaultNegativeInfinity;
    }
}
IcuFormatConstants.DefaultThousandSeparator = ",";
IcuFormatConstants.DefaultDecimalSeparator = ".";
IcuFormatConstants.DefaultNaNString = "NaN";
IcuFormatConstants.DefaultPositiveInfinity = "Infinity";
IcuFormatConstants.DefaultNegativeInfinity = "-Infinity";
export { IcuFormatConstants };
/**
 * A class representing the formatting context for number formatting.
 */
export class FormattingContext {
    constructor(options, constants, formatAbsoluteValue = false) {
        this.options = options;
        this.constants = constants;
        this.formatAbsoluteValue = formatAbsoluteValue;
    }
    /**
     * Derives a new context that is suitable to pass to nested formatters. The derived context
     * is set up so that the leaf formatter will format the absolute value of the number to format.
     *
     * @returns derived context
     */
    toNestedFormatCtx() {
        // Make a copy of the context with the formatAbsoluteValue set to true
        return new FormattingContext(this.options, this.constants, true);
    }
}
/**
 * A class representing the resolution context for number formatting.
 */
export class ResolutionContext {
    constructor(options, nestedFormatter = false) {
        this.options = options;
        this.nestedFormatter = nestedFormatter;
    }
    toNestedFormatCtx() {
        return new ResolutionContext(this.options, true);
    }
}
/**
 * Abstract base class for formatters that use the RegExp class to work with Unicode format strings and that produce
 * results as strings.
 *
 * The class serves as an adapter between public API which works with standard strings and RegExp.
 * It is intended to keep the RegExp as an implementation detail.
 */
export class FormatterUsingIcu {
    format(number, options, metadata) {
        if (metadata) {
            metadata.reset();
        }
        const constants = new IcuFormatConstants(options);
        const context = new FormattingContext(options, constants);
        return this.doFormat(context, number, metadata);
    }
    resolve(number, options) {
        const context = new ResolutionContext(options);
        return this.doResolve(context, number);
    }
}
/**
 * Example implementation of FixedLiteralFormatter.
 * You can define your own formatters by extending FormatterUsingIcu and implementing the abstract methods.
 */
export class FixedLiteralFormatter extends FormatterUsingIcu {
    constructor(literal, literalNoColors, colors) {
        super();
        this.literal = literal;
        this.literalNoColors = literalNoColors;
        this.colors = colors;
    }
    doFormat(ctx, _number, metadata) {
        if (metadata) {
            metadata.colors = this.colors;
            metadata.effectiveFormat = "";
        }
        if (!ctx.options.includeColors) {
            if (metadata) {
                metadata.effectiveFormat = this.literalNoColors;
            }
            return this.literalNoColors;
        }
        if (metadata) {
            metadata.effectiveFormat = this.literal;
        }
        return this.literal;
    }
    doResolve(ctx, _number) {
        if (!ctx.options.includeColors) {
            return this.literalNoColors;
        }
        return this.literal;
    }
    static compile(matchers, formatString) {
        let escapedLiteral = "";
        let escape = false;
        const colorMatcher = matchers.getColorMatcher();
        const customColorMatcher = matchers.getCustomColorMatcher();
        const customBgColorMatcher = matchers.getCustomBgColorMatcher();
        for (const c of formatString) {
            if (c === "\\") {
                escape = true;
            }
            else {
                escape = false;
                escapedLiteral += c;
            }
        }
        if (escape) {
            // backslash at end of literal should be used as-is
            escapedLiteral += "\\";
        }
        const escapedLiteralNoColors = stripColors(matchers, escapedLiteral);
        const colors = extractColorCoding(colorMatcher, customColorMatcher, customBgColorMatcher, escapedLiteral);
        // assuming FixedLiteralFormatter is a class with a constructor that accepts these parameters
        return new FixedLiteralFormatter(escapedLiteral, escapedLiteralNoColors, colors);
    }
}
//# sourceMappingURL=fmt_base.js.map